/**
 * @fileOverview The personnel planning walkthrough: the list of calendars and one calendar's three
 * gantt lanes. Pure data.
 * See docs/view-help.md and docs/view-help/personnelplanning.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/**
 * The drawer entry's own gate, rather than a copied list: personnel planning is a paid add-on, so
 * `nav-personnelplanning` asks for both the entitlement and `VIEW_PERSONNELPLANNING` — exactly what
 * every route of this feature carries (`router/routes/personnelplanning.js`). With the add-on
 * missing, the whole tour goes away and the trigger does not render.
 */
const PLANNING = "nav-personnelplanning"

/** Creating a calendar is its own right, and the create page is gated on it. */
const EDIT_PLANNING = [PERMISSIONS.EDIT_PERSONNELPLANNING]

/** Reading a project's phases — the related step on the project page needs one of the two. */
const VIEW_PHASES = [PERMISSIONS.VIEW_ASSIGNED_PROJECT_PHASE, PERMISSIONS.VIEW_FOREIGN_PROJECT_PHASE]

/** Reading the staff list — the related step on the employee list needs it. */
const VIEW_USERS = [PERMISSIONS.VIEW_USER_GENERAL]

/**
 * The two Zeiterfassung screens the plan shows up on need that add-on as well as this one, and the
 * Mitarbeiter screen is about other people's days. The steps keep `navEntry` on top of this, so a
 * workspace without the planning add-on never sees them either.
 */
const TIME_TRACKING = [FEATURE_SETS.TIME_TRACKING]
/** The timeline section the two of them share: one topic, Zeiterfassung, rather than two. */
const TIME = "TimeTracking"
const VIEW_FOREIGN_TIME = [PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]

/** The calendar this tour opens. One record per detail route, so every step names the same one. */
const CALENDAR = { params: { calendarId: "cal-fruehjahr" } }

const onOverview = stepsOn("PersonnelPlanning", { navEntry: PLANNING })
const onCalendar = stepsOn("PersonnelCalendar", { navEntry: PLANNING, at: CALENDAR })

// Where a calendar's contents come from: the workspace's absence kinds, the phases and the daily
// need of a project, and the staff that can be planned at all.
const onAbsenceTypes = stepsOn("SettingsOverview", { navEntry: PLANNING, at: { hash: "absence" } })
const onProject = stepsOn("ProjectDetail", {
  navEntry: PLANNING,
  permissions: VIEW_PHASES,
  at: { params: { projectId: "p-nord" }, hash: "phases" },
})
const onEmployees = stepsOn("EmployeeOverview", { navEntry: PLANNING, permissions: VIEW_USERS })

// And where the plan is read again outside the calendar: the Zeiterfassung asks the same assignments
// which project somebody was on (`TimeTrackingProjectResolver`) and whether they were there at all
// (`FrontendTimeTrackingAbsenceService`). Those two are the whole of it — nothing else in the product
// reads a personnel assignment.
const onMyTimes = stepsOn("TimeTrackingMy", { navEntry: PLANNING, featureSets: TIME_TRACKING, section: TIME })
const onTeamTimes = stepsOn("TimeTrackingReview", {
  navEntry: PLANNING,
  section: TIME,
  featureSets: TIME_TRACKING,
  permissions: VIEW_FOREIGN_TIME,
  // The fallback when the way fails: the Wochenraster is the page's second tab, and the tab lives in
  // the hash. The week it opens on is the current one either way, which is why the step pages back.
  at: { hash: "week" },
})

/**
 * Personnel planning: which of your people is on which site, week by week.
 *
 * Two views — the list of calendars and one calendar — and the calendar is the one that matters. It is also the one view of the product whose every verb is a
 * gesture: days are marked by dragging across them, work is handed out by dropping them on a project,
 * a bar is moved by being dragged and a menu is asked for with the right button. Six steps of the
 * calendar do exactly that, on the one hole the sample plan leaves in somebody's row, and the frame
 * keeps what they change (`KEEPS_WHAT_WAS_DONE` in `store/calendar.ts`).
 *
 * **Heute is what makes the board worth looking at.** The page opens scrolled to the earliest
 * montage date of its projects, which for the sample calendar is four months back, where the crew is
 * not yet planned. Every calendar step therefore belongs to a scene whose `enter` brings the board onto
 * today, so a jump from the timeline onto any of them lands on a board with bars in it — with
 * `showToday`, which presses Heute only when the board is not already there. Played straight through,
 * that is once on arrival and once after the enlarged view, which loses the offset.
 */
export const PERSONNEL_PLANNING_TOUR: ViewHelpTour = {
  key: "personnelplanning",
  icon: ENTITY_ICONS.personnelplanning,
  // The create form has no steps of its own; its trigger opens the tour at the top of the list.
  routes: ["PersonnelPlanning", "PersonnelCalendar", "PersonnelPlanningCreate"],
  // Where a calendar's contents are set up — the absence kinds it offers, the phases and the daily
  // need of a project, and the people it can plan — and then the two screens that read the plan back:
  // the clock's project suggestion and the Wochenraster's absent days. Played last, after the closing
  // step.
  related: ["SettingsOverview", "ProjectDetail", "EmployeeOverview", "TimeTrackingMy", "TimeTrackingReview"],
  scenes: [
    // A card dragged from "Alle" into "Angepinnt" and back down again. Irreversible, like every scene
    // that drags: played again it starts on a fresh list rather than on a card that may have moved.
    { key: "pin-drag", enter: [], exit: [], irreversible: true },
    // The card's own menu, likewise opened and closed. "Umbenennen" opens a dialog and is not pressed.
    {
      key: "card-options",
      enter: [{ do: "click", target: "planning-options-cal-halle" }],
      exit: [{ do: "escape" }],
    },
    // The calendar's Aktionen menu, opened by the step and closed again. Every entry either opens a dialog,
    // opens another window, or deletes — so none is pressed.
    { key: "actions-menu", enter: [], exit: [{ do: "escape" }] },
    // The board, brought onto today first — see the note above — for the steps that read it: the view
    // as a whole, then its three lanes.
    { key: "board", enter: [{ do: "showToday", target: "planning-today" }], exit: [] },
    // Paging and zooming, on a board brought onto today again (the enlarged view before it loses the
    // offset). The exit puts the day columns back to their normal width before anything else is shown
    // on them: the reset press is a single undo, like a filter's ✕, and it keeps a replayed scene from
    // zooming out a second and third time.
    {
      key: "navigation",
      enter: [{ do: "showToday", target: "planning-today" }],
      exit: [{ do: "click", target: "planning-zoom-reset" }],
    },
    // The six gestures, in one scene because each of them leaves the board in the state the next one
    // works on: days marked, an assignment made out of them, the bar that assignment drew. A jump into
    // the middle replays the ones before it at speed, so the board a step lands on is the board it is
    // explained on. `enter` presses Heute like the board scene above — the free days the whole
    // demonstration works on are next week's.
    //
    // The exit cancels the window the last step opens. Nothing else here has to be taken back: the
    // menus are closed by the step after them, and what the drags changed is the demonstration.
    {
      key: "gestures",
      enter: [{ do: "showToday", target: "planning-today" }],
      exit: [{ do: "click", target: "dynamic-dialog-cancel" }, { do: "escape" }],
      // Its drags turn free days into an assignment, which no exit undoes: played again, it starts on a
      // freshly loaded board.
      irreversible: true,
    },
    // One lane given the whole page, and handed back by the same button, which turns into its exit
    // while the view is enlarged. Heute comes *after* the enlarging, not before: folding the other
    // two lanes away re-measures the diagrams, which loses the horizontal offset and leaves the lane
    // at the start of the calendar, months before anybody is planned.
    {
      key: "enlarge",
      enter: [
        { do: "check", target: "planning-expand-employees" },
        { do: "showToday", target: "planning-today" },
      ],
      exit: [{ do: "uncheck", target: "planning-expand-employees" }],
    },
    // The menus of the name columns, opened one after the other and closed by the exit. `enter` presses
    // Heute: a jump straight here from the list must not land on a board scrolled months back.
    {
      key: "row-menus",
      enter: [{ do: "showToday", target: "planning-today" }],
      exit: [{ do: "escape" }],
    },
    // An absence entered on the board, as far as the kind is picked: the same free days as the gestures
    // marked, which are free again once their bar was moved on (see `boardAnchors.ts`), then their menu
    // and the window it opens. It drags, so it is replayed on a fresh board; the exit leaves the window
    // by Abbrechen — it is `persistent`, and saving it is a write.
    {
      key: "absence",
      enter: [{ do: "showToday", target: "planning-today" }],
      exit: [{ do: "click", target: "dynamic-dialog-cancel" }, { do: "escape" }],
      irreversible: true,
    },
    // The project's phases tab, pressed by the step itself.
    { key: "project-phases", enter: [], exit: [] },
    // The Wochenraster of the Zeiterfassung, opened and paged back to the week the sample holiday
    // falls in. All three presses are the step's own, so the reader watches the tab change and the
    // week move; nothing is undone afterwards, because the page is left for good.
    { key: "team-week", enter: [], exit: [] },
  ],
  steps: [
    // ── Die Kalenderliste ────────────────────────────────────────────────────
    //
    // No step on the search, the filter or the sort: the list holds two calendars, and each of those
    // controls explains itself. The tour starts on what a list of calendars does that a list of records
    // does not — the split between pinned and all.
    //
    // Both sections as one region: the split between them is the whole point, and each on its own
    // would be a step saying the same thing twice.
    onOverview("pinned", ["planning-pinned", "planning-all"], "mdi-pin", { placement: "bottom" }),
    // And how a card gets from one to the other: the unpinned calendar picked up and dropped into
    // "Angepinnt", then carried back down to "Alle", so the list the later steps point at is the one it
    // was. No zoom: both sections have to be in shot while the card travels between them. The frame
    // keeps the pin locally (`pinCalendar`), since the sandbox refuses the write.
    onOverview("pin-drag", ["planning-pinned", "planning-all"], "mdi-drag-variant", {
      placement: "right",
      scene: "pin-drag",
      zoom: false,
      permissions: EDIT_PLANNING,
      actions: [
        { do: "drag", from: "planning-card-cal-halle", to: "planning-pinned" },
        { do: "wait", ms: 500 },
        { do: "drag", from: "planning-card-cal-halle", to: "planning-all" },
      ],
    }),
    // The pin of the pinned calendar, which also carries the "several projects" badge beside it.
    onOverview("card-pin", "planning-pin-cal-fruehjahr", "mdi-pin-outline", { placement: "right" }),
    // Then down to the unpinned one, which has a single project and therefore a preview with bars. The
    // dot sits on its open icon — the way into the calendar — and the camera keeps the whole card,
    // which the text is about too.
    onOverview("card", "planning-open-cal-halle", "mdi-open-in-new", { placement: "right", zoom: false }),
    // No zoom: the region is a 28px button, and at the full step zoom the camera showed the menu and
    // nothing of the card it belongs to.
    onOverview("card-options", "planning-options-cal-halle", "mdi-dots-vertical", {
      placement: "left",
      scene: "card-options",
      zoom: false,
    }),
    onOverview("create", "planning-create", "mdi-plus", { placement: "bottom", permissions: EDIT_PLANNING }),

    // ── Ein Kalender ─────────────────────────────────────────────────────────
    //
    // What the view is for first, then its three lanes one by one — each with its text above it, so the
    // dot sits by the lane's heading rather than at its bottom edge — then the button that gives one of
    // them the whole page — explained right after the lanes it enlarges.
    onCalendar("intro", "planning-head", "mdi-calendar-account-outline", {
      placement: "bottom",
      scene: "board",
      zoom: false,
    }),
    onCalendar("projects", "planning-section-projects", "mdi-briefcase-outline", {
      placement: "top",
      scene: "board",
    }),
    onCalendar("employees", "planning-section-employees", "mdi-account-group-outline", {
      placement: "top",
      scene: "board",
    }),
    onCalendar("capacity", "planning-section-capacity", "mdi-chart-timeline-variant", {
      placement: "top",
      scene: "board",
    }),
    // The line between Projekte and Mitarbeiter, pointed at and not dragged: it follows pointer events,
    // and the walkthrough's drag sends mouse events. No zoom, so both lanes it divides stay in shot.
    onCalendar("resize", "planning-resize", "mdi-arrow-split-horizontal", {
      placement: "bottom",
      scene: "board",
      zoom: false,
    }),
    // The dot sits on the button that was pressed, not on the lane it enlarged — the reader has just
    // watched the pointer land there. No zoom: the point is the rest of the page folding away.
    onCalendar("enlarge", "planning-expand-employees", "mdi-fullscreen", {
      placement: "bottom",
      scene: "enlarge",
      zoom: false,
    }),
    // No zoom on either control of the top bar: both change the whole board below them, and zoomed
    // onto a row of buttons the reader would not see what they did. The navigation is used, not only
    // pointed at: a week on, and Heute back.
    onCalendar("dates", "planning-dates", "mdi-calendar-arrow-right", {
      placement: "bottom",
      scene: "navigation",
      zoom: false,
      actions: [
        { do: "click", target: "planning-next-week" },
        { do: "wait", ms: 700 },
        { do: "click", target: "planning-today" },
      ],
    }),
    // After the three lanes on purpose: the capacity chart does not re-anchor itself on a zoom change,
    // so at a changed day width its dates sit a few days off the other two. Undone by the scene's exit,
    // nothing after it is affected.
    onCalendar("zoom", "planning-zoom", "mdi-magnify-plus-outline", {
      placement: "bottom",
      scene: "navigation",
      zoom: false,
      // Out, a beat for the board to redraw, and back in again: both directions of the same control.
      actions: [
        { do: "click", target: "planning-zoom-out" },
        { do: "wait", ms: 700 },
        { do: "click", target: "planning-zoom-in" },
      ],
    }),
    // The calendar's own actions, shown by opening them: the pointer presses Aktionen, and the dot sits
    // on the menu it opened. The scene's exit closes it again.
    onCalendar("head", ["planning-actions", "planning-actions-menu"], "mdi-dots-horizontal-circle-outline", {
      placement: "bottom",
      scene: "actions-menu",
      actions: [{ do: "click", target: "planning-actions" }],
    }),
    // ── Mit der Maus planen ──────────────────────────────────────────────────
    //
    // Six gestures on the free fortnight in one person's row, each one the board's own way of doing
    // the thing it does. They come after the navigation on purpose: the zoom is back at its normal
    // day width by then (the navigation scene's exit), and both lanes are on screen, which a drag
    // between them needs.
    //
    // All six need the right to plan; with only `VIEW_PERSONNELPLANNING` the reader is shown a board
    // and no gestures they could make on it.
    onCalendar("mark", ["planning-day-from", "planning-day-to"], "mdi-cursor-default-click-outline", {
      placement: "bottom",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      actions: [{ do: "drag", from: "planning-day-from", to: "planning-day-to" }],
    }),
    // The right button on the marked days. The menu hangs off the block the board draws over a marked
    // day and not off the day itself, which answers a right-click with nothing — hence the second
    // anchor on the same place (`services/viewHelp/boardAnchors.ts`). The dot sits on that block and
    // the menu opens beside it, which is where a menu's own step always puts it.
    onCalendar("menu-days", "planning-day-marked", "mdi-menu", {
      placement: "bottom",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      actions: [{ do: "rightClick", target: "planning-day-marked" }],
    }),
    // The menu the step before it opened is in the way of the drag, so this step takes it away first.
    // The drop assigns the marked days to the project of the row they land on, with no form in
    // between, and the frame keeps the assignment instead of refetching it away.
    onCalendar("assign", ["planning-day-from", "planning-project-day"], "mdi-briefcase-plus-outline", {
      placement: "right",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      actions: [{ do: "escape" }, { do: "drag", from: "planning-day-marked", to: "planning-project-day" }],
    }),
    // And the bar that made, moved three days on: the day it is grabbed by (its middle) follows the
    // pointer to the stretch's fifth day. The last thing this scene does with it: a drag cannot
    // be taken back, so nothing after this may point at where the bar was.
    onCalendar("move", ["planning-span-new", "planning-day-later"], "mdi-cursor-move", {
      placement: "bottom",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      actions: [{ do: "drag", from: "planning-span-new", to: "planning-day-later" }],
    }),
    // The other menu: a bar's own. Aimed at the one the reader has just watched appear rather than at
    // a project's bar, which runs over months — a menu asked for in the middle of that one would first
    // drag the whole board back to the day the project started.
    onCalendar("menu-span", "planning-span-new", "mdi-menu-open", {
      placement: "right",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      actions: [{ do: "rightClick", target: "planning-span-new" }],
    }),
    // What one of its entries opens. The window is shown whole, and the scene's exit leaves it by its
    // own Abbrechen — it is `persistent`, so the Escape backstop would not take it away.
    onCalendar("editor", "dynamic-dialog", "mdi-calendar-edit", {
      placement: "bottom",
      scene: "gestures",
      permissions: EDIT_PLANNING,
      // A literal, not a label: the board's menus are written in the code in German and are the same
      // words for every reader. If they are ever translated, this has to become a label.
      actions: [{ do: "choose", text: "Zeitstrahl bearbeiten" }],
    }),

    // The name columns answer the right button too, with a menu for the row: first a project's, then a
    // person's. Only opened, never used — every entry is a preview, a page change or a form. The second
    // step closes the first menu before it asks for its own, as `assign` does.
    onCalendar("menu-project-row", "planning-name-project", "mdi-briefcase-outline", {
      permissions: EDIT_PLANNING,
      placement: "right",
      scene: "row-menus",
      actions: [{ do: "rightClick", target: "planning-name-project" }],
    }),
    onCalendar("menu-person-row", "planning-name-person", "mdi-account-outline", {
      permissions: EDIT_PLANNING,
      placement: "right",
      scene: "row-menus",
      actions: [{ do: "escape" }, { do: "rightClick", target: "planning-name-person" }],
    }),

    // Where to find this again — the trigger beside the calendar's own heading.
    onCalendar("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Five pages outside the feature, in both directions. First what the calendar takes from them:
    // the absence kinds it offers and their colours, the phases and the daily need a project brings,
    // and the people that can be planned at all — each step naming the setting and where it shows up
    // on the board. Then the two Zeiterfassung screens that read the finished plan back.
    //
    // The absence kinds open with where a kind is picked: on the board, in the window the marked days'
    // menu opens. The step is on the calendar but counted under the Abwesenheitsarten section, so the
    // two read as one topic — picked here, set up there.
    // The dot sits on the list of kinds in the window, not on the window's corner.
    onCalendar("absence-pick", "planning-absence-types", "mdi-calendar-remove-outline", {
      section: "SettingsOverview",
      placement: "top",
      scene: "absence",
      permissions: EDIT_PLANNING,
      actions: [
        { do: "drag", from: "planning-day-from", to: "planning-day-to" },
        { do: "rightClick", target: "planning-day-marked" },
        // Literals, not labels: the board's menu is written in German in the code, and the kind is the
        // sample world's own name.
        { do: "choose", text: "Abwesenheit eintragen" },
        { do: "click", target: "planning-absence-VACATION" },
      ],
    }),
    onAbsenceTypes("absence-types", "settings-absence-default", "mdi-calendar-remove-outline", {
      placement: "right",
    }),
    onProject("project-phases", "project-phases", "mdi-chart-timeline", {
      placement: "bottom",
      scene: "project-phases",
      actions: [{ do: "openTab", target: "project-tab-phases" }],
    }),
    onProject("project-capacity", "project-phases", "mdi-account-multiple-outline", {
      placement: "bottom",
      scene: "project-phases",
    }),
    onEmployees("staff", "employee-table", "mdi-account-hard-hat", { placement: "top" }),
    // And the other direction: the two places the plan is read back. The dot sits on the whole clock
    // panel rather than on the project field alone, because the line that names the Personalplanung
    // as the source stands under the field and would be cropped away by a camera on the field.
    // Both screens are one Zeiterfassung section on the timeline.
    onMyTimes("time-suggestion", "tt-my-clock", "mdi-play-circle-outline", { placement: "bottom" }),
    // The matrix on Zeiten prüfen, where Erika's two days of holiday are the same absence the board
    // draws — `◦` in the two cells of her row. Nothing is pressed: the matrix covers the page's whole
    // period (the month to date) rather than one week, so the days are already on screen. It used to
    // be a page of its own, then a tab, then a week grid this step had to page back twice.
    onTeamTimes("time-absence", "tt-matrix", "mdi-calendar-remove-outline", {
      // Below the matrix, into the empty half of the page. Above it the text sat over the page's own
      // heading and its filters.
      placement: "bottom",
      scene: "team-week",
    }),
  ],
}
